import React, { useState } from 'react';
import { 
  ArrowLeft, 
  ChevronRight, 
  Building, 
  Globe, 
  Trophy, 
  Handshake,
  Target,
  Zap,
  Shield,
  Layers,
  Network,
  Cpu,
  Rocket,
  TrendingUp
} from 'lucide-react';

interface PartnersProps {
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: string) => void;
}

interface Partner {
  name: string;
  logo: string;
  role: string;
  description: string;
  benefits: string[];
  website?: string;
  status: 'Active' | 'Integration' | 'Coming Soon';
}

interface PartnerCategory {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  count: number;
  partners: Partner[];
}

export default function PartnersPage({ onLaunchPlatform, onNavigateToPage }: PartnersProps) {
  const [activeTab, setActiveTab] = useState<string>('infrastructure');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const partnerCategories: PartnerCategory[] = [
    {
      id: 'infrastructure',
      title: 'Core Infrastructure',
      subtitle: 'Blockchain & Technology Foundation',
      icon: <Network className="w-5 h-5 text-[#24c35b]" />,
      count: 2,
      partners: [
        {
          name: 'Solana',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/Solana.png',
          role: 'Blockchain Infrastructure',
          description: 'High-performance blockchain powering EKKO with sub-second finality and minimal transaction costs for optimal trading experience.',
          benefits: [
            'Sub-second transaction finality',
            'Ultra-low gas fees for trading',
            'High throughput for peak trading volumes',
            'Robust ecosystem of DeFi protocols'
          ],
          status: 'Active'
        },
        {
          name: 'NVIDIA',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/nvidia.png',
          role: 'AI Computing Infrastructure',
          description: 'Advanced GPU computing power enabling EKKO\'s AI-driven trading algorithms and real-time market analysis capabilities.',
          benefits: [
            'High-performance AI model inference',
            'Real-time market data processing',
            'Advanced pattern recognition',
            'Scalable computing resources'
          ],
          status: 'Active'
        }
      ]
    },
    {
      id: 'trading',
      title: 'Trading Partners',
      subtitle: 'DEX & Liquidity Providers',
      icon: <TrendingUp className="w-5 h-5 text-[#24c35b]" />,
      count: 3,
      partners: [
        {
          name: 'Jupiter',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/jupiter.png',
          role: 'DEX Aggregator',
          description: 'Leading Solana DEX aggregator providing best-in-class swap routing and liquidity aggregation for optimal trading execution.',
          benefits: [
            'Best price routing across all DEXs',
            'Deep liquidity aggregation',
            'Smart order routing algorithms',
            'Minimal slippage optimization'
          ],
          status: 'Active'
        },
        {
          name: 'Raydium',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/raydium.png',
          role: 'Automated Market Maker',
          description: 'Premier Solana AMM providing concentrated liquidity and advanced trading features integrated directly into EKKO platform.',
          benefits: [
            'Concentrated liquidity pools',
            'Advanced order types support',
            'Yield farming opportunities',
            'Professional trading tools'
          ],
          status: 'Active'
        },
        {
          name: 'Pump.fun',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pumpfun.png',
          role: 'Token Launch Platform',
          description: 'Revolutionary token launch platform enabling EKKO users to discover and trade new tokens at launch with advanced sniping capabilities.',
          benefits: [
            'Early token launch detection',
            'Automated sniping tools',
            'Launch analytics and insights',
            'Fair launch mechanisms'
          ],
          status: 'Active'
        }
      ]
    },
    {
      id: 'ecosystem',
      title: 'Ecosystem Partners',
      subtitle: 'Tokens & Strategic Alliances',
      icon: <Globe className="w-5 h-5 text-[#24c35b]" />,
      count: 1,
      partners: [
        {
          name: 'PKIN',
          logo: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pkin.png',
          role: 'Strategic Token Partner',
          description: 'Strategic partnership with PKIN token, providing enhanced utility and cross-platform benefits for EKKO users.',
          benefits: [
            'Cross-platform utility benefits',
            'Enhanced staking rewards',
            'Exclusive trading features',
            'Strategic ecosystem alignment'
          ],
          status: 'Active'
        }
      ]
    }
  ];

  const handleTabClick = (tabId: string) => {
    if (tabId === activeTab) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(tabId);
      setIsTransitioning(false);
    }, 150);
  };

  const activeCategory = partnerCategories.find(cat => cat.id === activeTab);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Background Hero Image - Fixed to bottom */}
      <div className="fixed bottom-0 right-0 w-full h-full pointer-events-none z-1">
        <div className="relative h-full flex items-end justify-end">
          <div className="relative">
            <img 
              src="https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/216.png"
              alt="EKKO Partners Network"
              className="h-full w-auto object-contain object-bottom"
              style={{ 
                height: '100vh',
                maxWidth: '60vw'
              }}
            />
            {/* Left fade gradient */}
            <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent"></div>
            {/* Right fade gradient */}
            <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-white to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Architectural Lines */}
      <div className="fixed inset-0 pointer-events-none z-5">
        {/* Horizontal lines */}
        <div className="absolute top-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b] to-transparent"></div>
        <div className="absolute bottom-32 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        
        {/* Vertical lines */}
        <div className="absolute top-0 left-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        <div className="absolute top-0 right-1/4 w-0.5 h-full bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
        
        {/* Cross decorations */}
        <div className="absolute top-32 left-1/4 w-8 h-8 transform -translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        <div className="absolute top-32 right-1/4 w-8 h-8 transform translate-x-1/2 -translate-y-1/2">
          <div className="absolute inset-0 bg-[#24c35b] w-full h-0.5 top-1/2 transform -translate-y-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
          <div className="absolute inset-0 bg-[#24c35b] h-full w-0.5 left-1/2 transform -translate-x-1/2 rounded-full shadow-lg shadow-[#24c35b]/30"></div>
        </div>
        
        {/* Corner plus decorations */}
        <div className="absolute top-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute top-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 left-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
        <div className="absolute bottom-8 right-8 text-[#24c35b] text-2xl font-bold opacity-60">+</div>
      </div>

      {/* Background Text */}
      <div className="fixed inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
        <div className="text-gray-200 font-black select-none text-center space-y-8">
          <div className="text-[12vw] opacity-20 leading-none">PARTNERS</div>
          <div className="text-[10vw] opacity-15 leading-none">NETWORK</div>
          <div className="text-[8vw] opacity-10 leading-none">ECOSYSTEM</div>
        </div>
      </div>

      {/* Main Content */}
      <section className="relative z-10 min-h-screen py-20">
        <div className="container mx-auto px-4 max-w-7xl">
          
          {/* Header Section */}
          <div className="text-center mb-16 pt-8">
            <div className="space-y-6">
              <h1 className="text-6xl lg:text-7xl font-black leading-tight">
                <span className="bg-gradient-to-r from-[#24c35b] via-[#1eaa4e] to-[#24c35b] bg-clip-text text-transparent">
                  PARTNERS
                </span>
              </h1>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                Building the Future
                <br />
                <span className="text-[#24c35b]">Together</span>
              </h2>
            </div>
            
            <p className="text-xl text-gray-600 leading-relaxed mt-8 max-w-3xl mx-auto">
              Strategic partnerships with industry leaders, protocol pioneers, and ecosystem builders powering the EKKO platform.
            </p>
          </div>

          {/* Tabbed Interface */}
          <div className="max-w-6xl mx-auto">
            
            {/* Tab Navigation with Architectural Styling */}
            <div className="relative mb-12">
              {/* Architectural line under tabs */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
              
              <div className="flex justify-center">
                <div className="inline-flex bg-white rounded-2xl border-2 border-gray-200 p-2 shadow-lg">
                  {partnerCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleTabClick(category.id)}
                      className={`relative px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-3 group ${
                        activeTab === category.id
                          ? 'bg-[#24c35b] text-white shadow-lg'
                          : 'text-gray-600 hover:text-[#24c35b] hover:bg-gray-50'
                      }`}
                    >
                      {/* Architectural corner decoration for active tab */}
                      {activeTab === category.id && (
                        <>
                          <div className="absolute top-0 left-0 w-3 h-3">
                            <div className="absolute top-0 left-0 w-full h-0.5 bg-white/30"></div>
                            <div className="absolute top-0 left-0 w-0.5 h-full bg-white/30"></div>
                          </div>
                          <div className="absolute top-0 right-0 w-3 h-3">
                            <div className="absolute top-0 right-0 w-full h-0.5 bg-white/30"></div>
                            <div className="absolute top-0 right-0 w-0.5 h-full bg-white/30"></div>
                          </div>
                        </>
                      )}
                      
                      <div className={`w-5 h-5 transition-colors ${
                        activeTab === category.id ? 'text-white' : 'text-gray-400 group-hover:text-[#24c35b]'
                      }`}>
                        {category.icon}
                      </div>
                      
                      <div className="text-left">
                        <div className="text-sm font-bold">{category.title}</div>
                        <div className={`text-xs ${
                          activeTab === category.id ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {category.count} Partners
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className={`transition-all duration-300 ${
              isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
            }`}>
              {activeCategory && (
                <div className="space-y-8">
                  
                  {/* Category Header */}
                  <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-[#24c35b]/10 rounded-2xl flex items-center justify-center">
                        {activeCategory.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="text-2xl font-bold text-gray-900">{activeCategory.title}</h3>
                        <p className="text-[#24c35b] font-medium">{activeCategory.subtitle}</p>
                      </div>
                    </div>
                  </div>

                  {/* Partners Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {activeCategory.partners.map((partner, index) => (
                      <div 
                        key={index} 
                        className="relative group cursor-pointer transform hover:scale-105 transition-all duration-300"
                      >
                        {/* Architectural card styling */}
                        <div className="relative arch-card rounded-2xl overflow-hidden h-full hover:shadow-2xl transition-all duration-300 p-8">
                          {/* Architectural corner lines */}
                          <div className="absolute top-0 left-0 w-6 h-6 z-10">
                            <div className="absolute top-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                            <div className="absolute top-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                          </div>
                          <div className="absolute top-0 right-0 w-6 h-6 z-10">
                            <div className="absolute top-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                            <div className="absolute top-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                          </div>
                          <div className="absolute bottom-0 left-0 w-6 h-6 z-10">
                            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                            <div className="absolute bottom-0 left-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                          </div>
                          <div className="absolute bottom-0 right-0 w-6 h-6 z-10">
                            <div className="absolute bottom-0 right-0 w-full h-0.5 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                            <div className="absolute bottom-0 right-0 w-0.5 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                          </div>

                          {/* Partner Logo */}
                          <div className="relative mb-6">
                            <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-lg flex items-center justify-center p-4 group-hover:scale-110 transition-transform duration-300">
                              <img 
                                src={partner.logo}
                                alt={`${partner.name} logo`}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            
                            {/* Status Badge */}
                            <div className="absolute -top-2 -right-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                partner.status === 'Active' ? 'bg-green-100 text-green-700' :
                                partner.status === 'Integration' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {partner.status}
                              </span>
                            </div>
                          </div>

                          {/* Partner Info */}
                          <div className="text-center mb-6">
                            <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#24c35b] transition-colors duration-300">
                              {partner.name}
                            </h4>
                            <p className="text-[#24c35b] font-semibold mb-3 uppercase text-sm tracking-wider">
                              {partner.role}
                            </p>
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                              {partner.description}
                            </p>
                          </div>

                          {/* Benefits */}
                          <div className="space-y-2">
                            <h5 className="font-semibold text-gray-900 text-sm">Key Benefits:</h5>
                            {partner.benefits.slice(0, 3).map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-[#24c35b] rounded-full mt-1.5 flex-shrink-0"></div>
                                <span className="text-xs text-gray-600 leading-relaxed">{benefit}</span>
                              </div>
                            ))}
                            {partner.benefits.length > 3 && (
                              <div className="text-center mt-3">
                                <span className="text-xs text-[#24c35b] font-medium">
                                  +{partner.benefits.length - 3} more benefits
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Subtle architectural line */}
                          <div className="absolute bottom-8 left-8 right-8 h-0.5 bg-gradient-to-r from-transparent via-[#24c35b]/30 to-transparent"></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Call to Action */}
                  <div className="text-center mt-16">
                    <div className="relative arch-card rounded-3xl p-12 max-w-4xl mx-auto overflow-hidden">
                      {/* Architectural corner decorations */}
                      <div className="absolute top-0 left-0 w-8 h-8">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                        <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                      </div>
                      <div className="absolute top-0 right-0 w-8 h-8">
                        <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                        <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                      </div>
                      <div className="absolute bottom-0 left-0 w-8 h-8">
                        <div className="absolute bottom-0 left-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                        <div className="absolute bottom-0 left-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                      </div>
                      <div className="absolute bottom-0 right-0 w-8 h-8">
                        <div className="absolute bottom-0 right-0 w-full h-1 bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                        <div className="absolute bottom-0 right-0 w-1 h-full bg-[#24c35b] shadow-lg shadow-[#24c35b]/30"></div>
                      </div>

                      <div className="relative z-10">
                        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#24c35b] to-emerald-400 flex items-center justify-center text-white shadow-xl">
                          <Handshake className="h-8 w-8" />
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">
                          Ready to Experience Our <span className="text-gradient">Partner Network</span>?
                        </h3>
                        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                          Access the full power of our integrated partner ecosystem and revolutionize your trading experience.
                        </p>
                        
                        <button 
                          onClick={onLaunchPlatform}
                          className="btn-primary px-8 py-4 text-lg font-bold rounded-xl shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 group mx-auto"
                        >
                          <Trophy className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                          <span>Join Our Network</span>
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Partnership Stats - Bottom */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="arch-card px-8 py-4 rounded-xl border border-white/20 flex items-center gap-8">
          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">6 Strategic Partners</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">Global Network</span>
          </div>
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-[#24c35b]" />
            <span className="text-sm text-gray-600">Industry Leaders</span>
          </div>
        </div>
      </div>
    </div>
  );
} 